// frontend/src/pages/Tools.tsx
import { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Page from 'pages/Page';
import { ChainlitContext, useAuth } from '@chainlit/react-client';
import { Loader } from '@/components/Loader';

interface Tool {
  id: string;
  name: string;
  slug: string;
  category: string;
  description: string;
  provider: string;
  pricing: string;
  capabilities: string[];
  is_active: boolean;
  createdAt: string;
}

interface ToolFormData {
  name: string;
  slug: string;
  category: string;
  description: string;
  provider: string;
  pricing: string;
  capabilities: string;
  is_active: boolean;
}

const CATEGORIES = [
  'search',
  'research',
  'filesystem',
  'code_interpreter',
  'web_browsing',
  'database',
  'other'
];

const PRICING_OPTIONS = ['free', 'freemium', 'paid'];

const AdminToolsContent = () => {
  const { user, isAuthenticated, isReady } = useAuth();
  const apiClient = useContext(ChainlitContext);
  const navigate = useNavigate();
  
  const [tools, setTools] = useState<Tool[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedTool, setSelectedTool] = useState<Tool | null>(null);
  
  const [formData, setFormData] = useState<ToolFormData>({
    name: '',
    slug: '',
    category: 'search',
    description: '',
    provider: '',
    pricing: 'free',
    capabilities: '',
    is_active: true
  });
  const [formLoading, setFormLoading] = useState(false);

  useEffect(() => {
    if (!isReady) return;
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    fetchTools();
  }, [isAuthenticated, isReady, navigate]);

  const fetchTools = async () => {
    setLoading(true);
    setError('');
    try {
      const url = apiClient.buildEndpoint('/api/tools/');
      console.log('🔍 Fetching from URL:', url);
      console.log('🔍 API Client:', apiClient);
      
      const response = await fetch(url, { method: 'GET' });
      console.log('📡 Response status:', response.status);
      console.log('📡 Response ok:', response.ok);
  
      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ Error response:', errorText);
        throw new Error('Erreur lors du chargement des tools');
      }
  
      const data = await response.json();
      console.log('📦 Data received:', data);
      console.log('📦 Type of data:', typeof data);
      console.log('📦 Keys in data:', Object.keys(data));
      console.log('🔧 data.tools:', data.tools);
      console.log('🔧 Array.isArray(data.tools):', Array.isArray(data.tools));
      
      setTools(data.tools || []);
    } catch (err: any) {
      console.error('❌ Error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTool = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormLoading(true);
    setError('');

    try {
      const response = await fetch(
        apiClient.buildEndpoint('/api/tools/'),
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            ...formData,
            capabilities: formData.capabilities.split(',').map(c => c.trim()).filter(Boolean)
          })
        }
      );

      if (!response.ok) {
        const result = await response.json();
        throw new Error(result.detail || 'Erreur lors de la création');
      }

      setSuccess('✓ Tool créé avec succès !');
      setShowCreateModal(false);
      resetForm();
      fetchTools();

      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setFormLoading(false);
    }
  };

  const handleUpdateTool = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedTool) return;

    setFormLoading(true);
    setError('');

    try {
      const response = await fetch(
        apiClient.buildEndpoint(`/api/tools/${selectedTool.id}`),
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            ...formData,
            capabilities: formData.capabilities.split(',').map(c => c.trim()).filter(Boolean)
          })
        }
      );

      if (!response.ok) {
        const result = await response.json();
        throw new Error(result.detail || 'Erreur lors de la modification');
      }

      setSuccess('✓ Tool modifié avec succès !');
      setShowEditModal(false);
      setSelectedTool(null);
      resetForm();
      fetchTools();

      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setFormLoading(false);
    }
  };

  const handleDeleteTool = async () => {
    if (!selectedTool) return;

    setFormLoading(true);
    setError('');

    try {
      const response = await fetch(
        apiClient.buildEndpoint(`/api/tools/${selectedTool.id}`),
        { method: 'DELETE' }
      );

      if (!response.ok) {
        throw new Error('Erreur lors de la suppression');
      }

      setSuccess('✓ Tool supprimé avec succès !');
      setShowDeleteModal(false);
      setSelectedTool(null);
      fetchTools();

      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setFormLoading(false);
    }
  };

  const openEditModal = (tool: Tool) => {
    setSelectedTool(tool);
    setFormData({
      name: tool.name,
      slug: tool.slug,
      category: tool.category,
      description: tool.description,
      provider: tool.provider,
      pricing: tool.pricing,
      capabilities: tool.capabilities.join(', '),
      is_active: tool.is_active
    });
    setShowEditModal(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      slug: '',
      category: 'search',
      description: '',
      provider: '',
      pricing: 'free',
      capabilities: '',
      is_active: true
    });
  };

  const filteredTools = tools.filter(t => {
    const matchesSearch = 
      t.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      t.slug.toLowerCase().includes(searchQuery.toLowerCase()) ||
      t.description?.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesCategory = categoryFilter === 'all' || t.category === categoryFilter;
    
    return matchesSearch && matchesCategory;
  });

  const getCategoryIcon = (category: string) => {
    const icons: Record<string, string> = {
      search: '🔍',
      research: '📚',
      filesystem: '📁',
      code_interpreter: '💻',
      web_browsing: '🌐',
      database: '🗄️',
      other: '🔧'
    };
    return icons[category] || '🔧';
  };

  const getPricingBadge = (pricing: string) => {
    const badges: Record<string, { bg: string; text: string }> = {
      free: { bg: 'bg-green-100 dark:bg-green-900/30', text: 'text-green-700 dark:text-green-300' },
      freemium: { bg: 'bg-blue-100 dark:bg-blue-900/30', text: 'text-blue-700 dark:text-blue-300' },
      paid: { bg: 'bg-orange-100 dark:bg-orange-900/30', text: 'text-orange-700 dark:text-orange-300' }
    };
    return badges[pricing] || badges.free;
  };

  if (!isReady) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader className="!size-6" />
      </div>
    );
  }

  if (!isAuthenticated || !user) {
    return null;
  }

  return (
    <div className="flex flex-col h-full w-full overflow-auto">
      <div className="max-w-7xl mx-auto w-full p-6">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-6">
            <div>
              <button
                onClick={() => navigate('/admin')}
                className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground mb-4 transition-colors"
              >
                <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                Retour au dashboard
              </button>
              <h1 className="text-3xl font-bold mb-2">
                🔧 Tools & Intégrations
              </h1>
              <p className="text-muted-foreground">
                Gérez les outils disponibles pour les agents IA
              </p>
            </div>
            <button
              onClick={() => {
                resetForm();
                setShowCreateModal(true);
              }}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Nouveau tool
            </button>
          </div>

          {/* Filters */}
          <div className="flex gap-4 items-center">
            <div className="relative flex-1">
              <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <input
                type="text"
                placeholder="Rechercher un tool..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="flex h-10 w-full rounded-md border border-input bg-background pl-10 pr-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              />
            </div>
            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              className="flex h-10 rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <option value="all">Toutes catégories</option>
              {CATEGORIES.map(cat => (
                <option key={cat} value={cat}>
                  {getCategoryIcon(cat)} {cat}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Messages */}
        {success && (
          <div className="mb-6 rounded-md border border-green-500 bg-green-50 dark:bg-green-950/50 p-4 text-sm text-green-700 dark:text-green-300">
            <div className="flex items-center gap-2">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              {success}
            </div>
          </div>
        )}

        {error && (
          <div className="mb-6 rounded-md border border-destructive bg-destructive/10 p-4 text-sm text-destructive">
            <div className="flex items-center gap-2">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              {error}
            </div>
          </div>
        )}

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="rounded-lg border bg-card p-4">
            <p className="text-sm text-muted-foreground mb-1">Total tools</p>
            <p className="text-2xl font-bold">{tools.length}</p>
          </div>
          <div className="rounded-lg border bg-card p-4">
            <p className="text-sm text-muted-foreground mb-1">Actifs</p>
            <p className="text-2xl font-bold">{tools.filter(t => t.is_active).length}</p>
          </div>
          <div className="rounded-lg border bg-card p-4">
            <p className="text-sm text-muted-foreground mb-1">Gratuits</p>
            <p className="text-2xl font-bold">{tools.filter(t => t.pricing === 'free').length}</p>
          </div>
          <div className="rounded-lg border bg-card p-4">
            <p className="text-sm text-muted-foreground mb-1">Catégories</p>
            <p className="text-2xl font-bold">{new Set(tools.map(t => t.category)).size}</p>
          </div>
        </div>

        {/* Tools Table */}
        {loading ? (
          <div className="flex items-center justify-center p-12 rounded-lg border bg-card">
            <Loader className="!size-8" />
          </div>
        ) : filteredTools.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground rounded-lg border bg-card">
            <svg className="w-16 h-16 mx-auto mb-4 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            <p className="text-lg font-medium mb-2">
              {searchQuery || categoryFilter !== 'all' ? 'Aucun résultat' : 'Aucun tool'}
            </p>
            <p className="text-sm">
              {searchQuery || categoryFilter !== 'all' ? 'Essayez de modifier vos filtres' : 'Créez le premier tool'}
            </p>
          </div>
        ) : (
          <div className="rounded-lg border bg-card overflow-hidden">
            <table className="w-full">
              <thead className="border-b bg-muted/50">
                <tr>
                  <th className="text-left p-4 font-medium">Tool</th>
                  <th className="text-left p-4 font-medium">Catégorie</th>
                  <th className="text-left p-4 font-medium">Provider</th>
                  <th className="text-left p-4 font-medium">Pricing</th>
                  <th className="text-left p-4 font-medium">Capacités</th>
                  <th className="text-left p-4 font-medium">Statut</th>
                  <th className="text-right p-4 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {filteredTools.map((tool) => {
                  const pricingBadge = getPricingBadge(tool.pricing);
                  return (
                    <tr key={tool.id} className="hover:bg-muted/30 transition-colors">
                      <td className="p-4">
                        <div className="flex items-center gap-3">
                          <div className="text-2xl">{getCategoryIcon(tool.category)}</div>
                          <div>
                            <p className="font-medium">{tool.name}</p>
                            <p className="text-xs text-muted-foreground font-mono">{tool.slug}</p>
                          </div>
                        </div>
                      </td>
                      <td className="p-4">
                        <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-muted">
                          {tool.category}
                        </span>
                      </td>
                      <td className="p-4 text-sm">{tool.provider}</td>
                      <td className="p-4">
                        <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${pricingBadge.bg} ${pricingBadge.text}`}>
                          {tool.pricing}
                        </span>
                      </td>
                      <td className="p-4 text-sm text-muted-foreground">
                        {tool.capabilities.length} caps
                      </td>
                      <td className="p-4">
                        <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${
                          tool.is_active
                            ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300'
                            : 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300'
                        }`}>
                          {tool.is_active ? 'Actif' : 'Inactif'}
                        </span>
                      </td>
                      <td className="p-4">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => openEditModal(tool)}
                            className="p-2 hover:bg-accent rounded-md transition-colors"
                            title="Modifier"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                          </button>
                          <button
                            onClick={() => {
                              setSelectedTool(tool);
                              setShowDeleteModal(true);
                            }}
                            className="p-2 text-destructive hover:bg-destructive/10 rounded-md transition-colors"
                            title="Supprimer"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      {(showCreateModal || showEditModal) && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card rounded-lg max-w-2xl w-full p-6 space-y-6">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-semibold">
                {showCreateModal ? 'Créer un tool' : 'Modifier le tool'}
              </h2>
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  setShowEditModal(false);
                  setSelectedTool(null);
                  resetForm();
                }}
                className="text-muted-foreground hover:text-foreground"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <form onSubmit={showCreateModal ? handleCreateTool : handleUpdateTool} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium">Nom *</label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  />
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium">Slug *</label>
                  <input
                    type="text"
                    required
                    pattern="[a-z0-9_-]+"
                    value={formData.slug}
                    onChange={(e) => setFormData({ ...formData, slug: e.target.value.toLowerCase() })}
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm font-mono focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium">Description</label>
                <textarea
                  rows={2}
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                />
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium">Catégorie *</label>
                  <select
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  >
                    {CATEGORIES.map(cat => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium">Provider</label>
                  <input
                    type="text"
                    value={formData.provider}
                    onChange={(e) => setFormData({ ...formData, provider: e.target.value })}
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  />
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium">Pricing *</label>
                  <select
                    value={formData.pricing}
                    onChange={(e) => setFormData({ ...formData, pricing: e.target.value })}
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  >
                    {PRICING_OPTIONS.map(price => (
                      <option key={price} value={price}>{price}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium">Capacités (séparées par des virgules)</label>
                <textarea
                  rows={2}
                  value={formData.capabilities}
                  onChange={(e) => setFormData({ ...formData, capabilities: e.target.value })}
                  placeholder="search, query, filter"
                  className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                />
              </div>

              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  className="rounded"
                />
                <span className="text-sm">Tool actif</span>
              </label>

              <div className="flex gap-3 pt-4 border-t">
                <button
                  type="submit"
                  disabled={formLoading}
                  className="flex-1 inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
                >
                  {formLoading ? 'Enregistrement...' : showCreateModal ? 'Créer' : 'Modifier'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateModal(false);
                    setShowEditModal(false);
                    setSelectedTool(null);
                    resetForm();
                  }}
                  disabled={formLoading}
                  className="px-4 py-2 rounded-md border border-input hover:bg-accent"
                >
                  Annuler
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Modal */}
      {showDeleteModal && selectedTool && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card rounded-lg max-w-md w-full p-6 space-y-4">
            <h2 className="text-xl font-semibold">Confirmer la suppression</h2>
            <p className="text-muted-foreground">
              Êtes-vous sûr de vouloir supprimer le tool{' '}
              <strong>{selectedTool.name}</strong> ?
              Cette action est irréversible.
            </p>
            <div className="flex gap-3">
              <button
                onClick={handleDeleteTool}
                disabled={formLoading}
                className="flex-1 inline-flex items-center justify-center rounded-md bg-destructive px-4 py-2 text-sm font-medium text-destructive-foreground hover:bg-destructive/90 disabled:opacity-50"
              >
                {formLoading ? 'Suppression...' : 'Supprimer'}
              </button>
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  setSelectedTool(null);
                }}
                disabled={formLoading}
                className="px-4 py-2 rounded-md border border-input hover:bg-accent"
              >
                Annuler
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default function AdminTools() {
  return (
    <Page>
      <AdminToolsContent />
    </Page>
  );
}